import { useMemo, useRef, useCallback, useState } from 'react';
import { toPng } from 'html-to-image';
import { EPaymentMethod, EArea, TFemalePrice, TReceiptMethods } from 'types';
import { formatDate, formatNumber } from 'utils';
import { TEXT_STRING } from '../../../constants';
import { Button } from 'components';

type Props = {
  receiptNo: string;
  amount: number;
  methods: TReceiptMethods;
  createdDatetime: string;
  females: TFemalePrice[];
  areaName: string;
};

const receiptStr = TEXT_STRING.RECEIPT;

const Receipt = ({
  receiptNo,
  amount,
  methods,
  createdDatetime,
  females,
  areaName,
}: Props) => {
  const receiptRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [address, setAddress] = useState<string>('');
  const isDownloadingRef = useRef<boolean>(false);

  const purposeText = useMemo(() => {
    const methodKeys = Object.keys(methods || {});
    if (
      methodKeys.includes(EPaymentMethod.CARD) ||
      methodKeys.includes(EPaymentMethod.VISA)
    ) {
      return receiptStr.PURPOSE_CARD;
    }
    if (
      methodKeys.includes(EPaymentMethod.BANK) ||
      methodKeys.includes(EPaymentMethod.POSTPAY)
    ) {
      return receiptStr.PURPOSE_BANK;
    }
    if (methodKeys.includes(EPaymentMethod.POINT)) {
      return receiptStr.PURPOSE_POINT;
    }
    return receiptStr.PURPOSE_CASH;
  }, [methods]);

  const receiptAmount = useMemo(() => {
    if (!methods || Object.keys(methods).length === 0) {
      return amount;
    }
    return Object.values(methods).reduce(
      (sum, method) => sum + (method.amount || 0) + (method.fee || 0),
      0,
    );
  }, [amount, methods]);

  const { companyAddress, companyName } = useMemo(() => {
    if (areaName?.includes(EArea.KANAGAWA)) {
      return {
        companyAddress: receiptStr.COMPANY_ADDRESS_FC,
        companyName: receiptStr.COMPANY_NAME_FC,
      };
    }
    return {
      companyAddress: receiptStr.COMPANY_ADDRESS,
      companyName: receiptStr.COMPANY_NAME,
    };
  }, [areaName]);

  const handleDownload = useCallback(async () => {
    if (!receiptRef.current || isDownloadingRef.current) return;
    isDownloadingRef.current = true;
    setIsDownloading(true);
    try {
      const dataUrl = await toPng(receiptRef.current, {
        pixelRatio: 2,
        backgroundColor: '#ffffff',
        skipFonts: true,
      });
      const link = document.createElement('a');
      link.download = `receipt_${receiptNo}.png`;
      link.href = dataUrl;
      link.click();
    } finally {
      isDownloadingRef.current = false;
      setIsDownloading(false);
    }
  }, [receiptNo]);

  const handleAddressChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setAddress(e.target.value);
    },
    [],
  );

  return (
    <div className="mt-6">
      <div className="mx-4">
        <div
          ref={receiptRef}
          className="p-8 bg-white border border-black text-black"
        >
          <div className="flex justify-between items-start mb-6">
            <h2 className="text-2xl font-bold">{receiptStr.TITLE}</h2>
            <div className="text-left text-sm">
              <p className="pb-1 border-b border-black">
                {receiptStr.NO_PREFIX} {receiptNo}
              </p>
              <p className="pb-1 mt-1 border-b border-black">
                {formatDate(createdDatetime, 'LL')}
              </p>
            </div>
          </div>

          <div className="mb-6">
            <div className="inline-flex items-end pb-2 border-b border-black">
              <input
                type="text"
                value={address}
                onChange={handleAddressChange}
                className="text-lg w-[200px] border-none"
              />
              <span className="ml-2 text-xl">{receiptStr.HONORIFIC}</span>
            </div>
          </div>

          <div className="text-center mb-6 pb-2 mx-16 border-b border-black">
            <p className="text-3xl font-bold tracking-wider">
              {receiptStr.CURRENCY} {formatNumber(receiptAmount)}　－
            </p>
          </div>

          <div className="mb-4 flex justify-center">
            <div className="text-left">
              <p className="text-sm">
                {receiptStr.PURPOSE.replace('$value', purposeText)}
              </p>
              <p className="text-sm mt-1">{receiptStr.CONFIRMATION}</p>
            </div>
          </div>

          <div className="flex gap-4 items-start text-xs">
            <div className="flex-1 flex flex-wrap gap-x-3 gap-y-1">
              {females.map((female, idx) => (
                <span key={`${female.femaleId}-${idx}`}>
                  【ID:{female.femaleId}】
                  <a
                    href={female.profileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-black"
                  >
                    {female.profileUrl}
                  </a>
                </span>
              ))}
            </div>
            <div className="text-left w-[180px]">
              <p>{companyAddress}</p>
              <p>{companyName}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 mt-4 mb-6">
        <Button
          bold
          block
          loading={isDownloading}
          onClick={handleDownload}
          disabled={isDownloading}
          textSize="text-sm"
        >
          {receiptStr.DOWNLOAD}
        </Button>
      </div>
    </div>
  );
};

export default Receipt;
